import React from "react";
import { Accordion } from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";
import { AccordionSkeleton } from "../../components";
import { ApiHelper, PlaylistInterface, ToastHelper } from "../../helpers";

interface Props {
  refreshKey: string
}

export const PlaylistsSidebar: React.FC<Props> = props => {
  const location = useLocation();
  const [playlists, setPlaylists] = React.useState<PlaylistInterface[]>(null);
  const [selectedPlaylist, setSelectedPlaylist] = React.useState<PlaylistInterface>(null);
  const [selectMode, setSelectMode] = React.useState(false);
  const [selectedIds, setSelectedIds] = React.useState<number[]>([]);

  const init = () => {
    ApiHelper.get<PlaylistInterface[]>("/playlists")
      .then((data) => setPlaylists(data || []))
      .catch(err => {
        console.error("Failed to load playlists:", err);
        ToastHelper.error("Failed to load playlists");
      });
  }

  const updateSelected = () => {
    let result: PlaylistInterface = null;
    const parts = window.location.href.split("?")[0].split("/");
    const id = parseInt(parts[parts.length - 1]);
    playlists?.forEach(p => { if (p.id === id) result = p; })
    setSelectedPlaylist(result);
  }

  React.useEffect(init, [props.refreshKey])
  React.useEffect(updateSelected, [playlists, location.pathname]) //eslint-disable-line

  const toggleSelectMode = () => {
    setSelectMode(!selectMode);
    setSelectedIds([]);
  };

  const togglePlaylistSelection = (id: number) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(sid => sid !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handleSelectAll = () => {
    const allIds = playlists?.map(p => p.id) || [];
    if (selectedIds.length === allIds.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(allIds);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm("Are you sure you wish to delete " + selectedIds.length + " playlist(s)?")) return;
    try {
      await Promise.all(selectedIds.map(id => ApiHelper.delete("/playlists/" + id)));
      ToastHelper.success("Deleted " + selectedIds.length + " playlist(s)");
      setSelectedIds([]);
      setSelectMode(false);
      init();
    } catch (err) {
      console.error("Failed to delete playlists:", err);
      ToastHelper.error("Failed to delete some playlists");
    }
  };

  const getPlaylistRows = (playlistType: string) => {
    const result: React.ReactElement[] = [];

    const filtered = (playlistType === "feed")
      ? playlists?.filter(p => p.subscriptionId > 0)
      : playlists?.filter(p => !(p.subscriptionId > 0));

    filtered?.forEach(p => {
      let className = (p.id === selectedPlaylist?.id) ? "active" : "";
      if (selectMode) {
        const isChecked = selectedIds.includes(p.id);
        result.push(
          <li key={"p" + p.id} className="nav-item">
            <span className={className} style={{cursor: "pointer"}} onClick={() => togglePlaylistSelection(p.id)}>
              <input type="checkbox" checked={isChecked} onChange={() => togglePlaylistSelection(p.id)} style={{marginRight: 8}} onClick={(e) => e.stopPropagation()} />
              {p.name || "(No name)"}
            </span>
          </li>
        );
      } else {
        result.push(
          <li key={"p" + p.id} className="nav-item">
            <Link className={className} to={"/playlists/" + p.id}>{p.name || "(No name)"}</Link>
          </li>
        );
      }
    });
    return <ul className="nav flex-column">{result}</ul>;
  }

  const getSection = (eventKey: string, title: string, addLink: string, contents: React.ReactElement) => {
    return (
      <Accordion.Item eventKey={eventKey}>
        <Accordion.Header>
          <span className="accordion-title-text">{title}</span>
          <Link
            to={addLink}
            className="sidebar-accordion-add-btn"
            title={`Create new ${title.toLowerCase()}`}
            onClick={(e) => e.stopPropagation()}
          >
            <i className="fas fa-plus"></i>
          </Link>
        </Accordion.Header>
        <Accordion.Body>{contents}</Accordion.Body>
      </Accordion.Item>
    )
  }

  const getAccordion = () => {
    if (playlists === null) return <AccordionSkeleton sections={2} />;

    const feedSection = getSection("feeds", "Feeds", "/playlists?mode=feed", getPlaylistRows("feed"));
    const customSection = getSection("custom", "Custom", "/playlists?mode=custom", getPlaylistRows("custom"));

    return (
      <Accordion id="playlistsSidebar" defaultActiveKey="custom">
        {feedSection}
        {customSection}
      </Accordion>
    );
  }

  return (
    <div className="sidebar-container">
      <div className="sidebar-header">
        <h3 className="sidebar-title">Playlists</h3>
        <div className="sidebar-actions">
          <button
            className={selectMode ? "sidebar-icon-btn active" : "sidebar-icon-btn"}
            onClick={toggleSelectMode}
            title={selectMode ? "Exit select mode" : "Select playlists"}
          >
            <i className="fas fa-check-square"></i>
          </button>
        </div>
      </div>
      {selectMode && (
        <div className="sidebar-bulk-actions" style={{display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", borderBottom: "1px solid #dee2e6"}}>
          <button className="btn btn-sm btn-outline-secondary" onClick={handleSelectAll}>
            {selectedIds.length === (playlists?.length || 0) ? "Deselect All" : "Select All"}
          </button>
          <button className="btn btn-sm btn-danger" onClick={handleBulkDelete} disabled={selectedIds.length === 0}>
            <i className="fas fa-trash"></i> Delete ({selectedIds.length})
          </button>
        </div>
      )}
      <div className="sidebar-content">
        {getAccordion()}
      </div>
    </div>
  );
};